// @flow

import React, { useState, useContext } from 'react';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import { Box } from '@latitude/box';
import FeaturesSlider from '@/components/FeaturesSlider/FeaturesSlideV2';
import { StickyCta } from '@latitude/sticky-cta';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import StickyNavigation from '@/components/StickyNavigation/StickyNavigationV2';
import { Heading4 } from '@latitude/heading';
import {
  REBRAND_COLOR,
  PADDING,
  BREAKPOINT,
  COLOR,
  MARGIN,
  POSITION
} from '@latitude/core/utils/constants';
import Layout from '../../components/layout';
import MobileAppInstallPrompts from '@latitude/mobile-app-install-prompt/MobileAppInstallPrompt';
import PageData from '../../data/pages/travel-loan.json';
import footerData from '../../data/json/footer.json';
import PageHeaderSection from '@/components/PageLayout/PageHeaderSection';
import { featuresSliderIE11Fix } from '../../utils/stylesUtils';
import useApplyUrl from '../../hooks/useApplyUrl';
import SoftQuoteContext from '../../context/SoftQuoteContext';
import SvgInline from '@latitude/svg-inline';
import { PageContext } from '../../context/PageContext';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import Section from '@latitude/section-rebrand';
import ProductComparisonSection from '@/components/ProductComparisonSection/ProductComparisonSectionV2';
import CardCollection from '@/components/lab-components/CardCollection/CardCollectionV2';
import ProofPointCollection from '@/components/lab-components/ProofPointCollection/ProofPointCollectionV2';
import PromoBanner from '@/components/lab-components/PromoBanner/PromoBannerV2';
import Faq from '@/components/Faq/FaqV2/Faq';
import PersonalLoanCalculator from '@/components/PersonalLoanCalculatorRebrand/PersonalLoanCalculator';
import Button from '@latitude/button-rebrand';
import Typography from '@latitude/typography';
import { SOFT_QUOTE_LOANS_URL } from '../../utils/softQuoteUtil';

/*
 * Travel Loan Page
 */
const TravelLoanPage = () => {
  const [isNavSticky, setIsNavSticky] = useState(false);
  const { applyUrl, setApplyUrl } = useApplyUrl();
  const COLOUR_PALE_BLUE = '#E5F6FF';

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
  const featureSliderData = state?.featureSliderData || [
    {
      heading: 'Why choose a Gem Loan?',
      featureCards: PageData.content.whyChooseLatitude
    }
  ];
  /** - END - */
  const getEstimateUrl = 'https://loans.portal.gemfinance.co.nz/s/quote';

  return (
    <Layout noMetaKeywords customFooter={footerData}>
      <MobileAppInstallPrompts />
      <main className="navigation-spacer travel-loan-page">
        <Box backgroundColor={COLOR.GREY6}>
          <Helmet>
            <link
              rel="canonical"
              href="https://www.gemfinance.co.nz/loans/travel-loans/"
            />
            <title>
              Personal Loan for Travels and Holidays - Gem by Latitude
            </title>
            <meta
              name="description"
              content="Our personal loan for travel or holiday is the ticket to turning your dream holiday a reality. Get a quote online in under 2 minutes."
              noIndex
            />
          </Helmet>
          <HeroV2
            {...(state?.heroBannerData?.[0] ||
              PageData?.contentful?.heroBannerData?.[0])}
          />
          <SoftQuoteContext.Provider value={{ applyUrl, setApplyUrl }}>
            <StickyNavigation
              items={PageData?.contentful?.inPageNavData?.items}
              phoneNumber={PageData.content.phoneNumber}
              isSticky={isNavSticky}
              ctaHref={applyUrl}
              ctaTarget="_self"
              ctaText="Get rate estimate"
              onStickyNavStateChange={setIsNavSticky}
              offsetElem="[data-sticky-navigation-offset]"
              trackId="sticky-nav-get-started"
              trackEventData={{
                category: 'cta',
                action: 'quote-link',
                label: 'Get rate estimate',
                location: 'Sticky Navigation'
              }}
              trackProductId={['PLNZLF-WEB']}
              {...state?.inPageNavData?.[0]}
            />
          </SoftQuoteContext.Provider>

          <FeaturesSlider
            key={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).heading
            }
            heading={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).heading
            }
            id="why-us"
            className="why-choose bg-f8f8f8"
            subheading={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).description
            }
            backgroundColor={
              state?.featureSliderData?.[0]?.backgroundColor?.value ||
              PageData?.contentful?.featureSliderData[0]?.backgroundColor?.value
            }
            data={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).featureCards
            }
          />
          <div
            css={`
              background-color: ${REBRAND_COLOR.BLACK_20};
              padding-top: 32px;
              padding-bottom: 32px;
              && .typography-base__sh1 {
                margin-bottom: 30px !important;
              }
            `}
          >
            <Section id="check-your-rate">
              <Typography.SH1 color={REBRAND_COLOR.BLACK} semibold>
                Get an estimate of your repayments
              </Typography.SH1>
              <PersonalLoanCalculator
                applyUrl={getEstimateUrl}
                applyCTATrackProductId={['PLNZLF-WEB']}
              />
            </Section>
          </div>

          <ProductComparisonSection
            id="compare-products"
            {...state?.productComparisonData?.[0]}
          />

          <ProofPointCollection
            id="calculators-and-tools"
            data={
              state?.proofPointCollectionData?.[0] ||
              PageData?.contentful?.proofPointCollectionData?.[0]
            }
          />

          <CardCollection
            id="applying-is-simple"
            data={
              state?.cardCollectionData?.[0] ||
              PageData?.contentful?.cardCollectionData?.[0]
            }
          />

          <Faq data={PageData.content.faq} {...state?.faqData?.[0]} />

          <ImportantInformation
            data={require('../../data/pages/travel-loan.json')}
            css={`
              background-color: ${REBRAND_COLOR.STONE};
              overflow-x: hidden;
              .row {
                @media (min-width: 1600px) {
                  padding-left: ${PADDING.P32};
                  padding-right: ${PADDING.P32};
                }
              }
            `}
            {...{
              ...state?.importantInfoData?.[0],
              sectionOneColummClasses: 'col-12 col-lg-6',
              sectionTwoColummClasses: 'col-12 col-lg-6'
            }}
          />
        </Box>
      </main>
    </Layout>
  );
};

const HomeButton = styled(Button)`
  && {
    padding: 15px 45px;
    background-color: #21a637;
    color: #fff !important;
    white-space: nowrap;
    font-size: 22px;
  }

  margin-bottom: 8px;
  &:hover {
    box-shadow: 0 1px 13px 0 rgba(0, 0, 0, 0.4);
    transition: all 150ms ease-in-out;
  }
`;

export default TravelLoanPage;
